/**
 * Tenant-Aware Service Layer
 * 
 * This file provides tenant-safe wrappers around database operations.
 * All queries automatically inject tenant_id to prevent cross-tenant data leakage.
 * 
 * CRITICAL: Use these services instead of direct supabase calls after migration.
 */

import { supabase } from './supabase';
import { 
  selectWithTenant, 
  insertWithTenant, 
  updateWithTenant, 
  deleteWithTenant,
  withTenant,
  withTenantData
} from './supabase-helpers';
import type { Patient, Doctor, Appointment, Invoice, InvoiceItem, Payment } from './supabase';

/**
 * Tenant-Aware Patient Service
 * 
 * All operations are scoped by tenant_id
 */
export const tenantPatientService = {
  async getAll(tenantId: string, search?: string) {
    let query = supabase
      .from('patients')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    
    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Patient[];
  },

  async getById(tenantId: string, id: string) {
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (error) throw error;
    return data as Patient;
  },

  async create(tenantId: string, patient: Omit<Patient, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await insertWithTenant(
      'patients',
      { ...patient, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    return data[0] as Patient;
  },

  async update(tenantId: string, id: string, patient: Partial<Patient>) {
    const { data, error } = await updateWithTenant(
      'patients',
      id,
      { ...patient, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    return data[0] as Patient;
  },

  async delete(tenantId: string, id: string) {
    const { error } = await deleteWithTenant('patients', id, tenantId);
    if (error) throw error;
  }
};

/**
 * Tenant-Aware Doctor Service
 * 
 * All operations are scoped by tenant_id
 */
export const tenantDoctorService = {
  async getAll(tenantId: string, search?: string) {
    let query = supabase
      .from('doctors')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    
    if (search) {
      query = query.or(`name.ilike.%${search}%,specialization.ilike.%${search}%,department.ilike.%${search}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Doctor[];
  },

  async getById(tenantId: string, id: string) {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (error) throw error;
    return data as Doctor;
  },

  async create(tenantId: string, doctor: Omit<Doctor, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await insertWithTenant(
      'doctors',
      { ...doctor, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    return data[0] as Doctor;
  },

  async update(tenantId: string, id: string, doctor: Partial<Doctor>) {
    const { data, error } = await updateWithTenant(
      'doctors',
      id,
      { ...doctor, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    return data[0] as Doctor;
  },

  async delete(tenantId: string, id: string) {
    const { error } = await deleteWithTenant('doctors', id, tenantId);
    if (error) throw error;
  }
};

/**
 * Tenant-Aware Appointment Service
 * 
 * All operations are scoped by tenant_id
 */
export const tenantAppointmentService = {
  async getAll(tenantId: string, startDate?: string, endDate?: string) {
    let query = supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('tenant_id', tenantId)
      .order('appointment_date', { ascending: true })
      .order('start_time', { ascending: true });
    
    if (startDate) {
      query = query.gte('appointment_date', startDate);
    }
    if (endDate) {
      query = query.lte('appointment_date', endDate);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Appointment[];
  },

  async getByDoctor(tenantId: string, doctorId: string) {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('tenant_id', tenantId)
      .eq('doctor_id', doctorId)
      .order('appointment_date', { ascending: true })
      .order('start_time', { ascending: true });
    
    if (error) throw error;
    return data as Appointment[];
  },

  async getById(tenantId: string, id: string) {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (error) throw error;
    return data as Appointment;
  },

  async create(tenantId: string, appointment: Omit<Appointment, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'patient' | 'doctor'>) {
    const { data, error } = await insertWithTenant(
      'appointments',
      { ...appointment, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    
    const { data: fullData, error: fetchError } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('id', data[0].id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (fetchError) throw fetchError;
    return fullData as Appointment;
  },

  async update(tenantId: string, id: string, appointment: Partial<Appointment>) {
    const { data, error } = await updateWithTenant(
      'appointments',
      id,
      { ...appointment, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    
    const { data: fullData, error: fetchError } = await supabase
      .from('appointments')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*)
      `)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (fetchError) throw fetchError;
    return fullData as Appointment;
  },

  async delete(tenantId: string, id: string) {
    const { error } = await deleteWithTenant('appointments', id, tenantId);
    if (error) throw error;
  }
};

/**
 * Tenant-Aware Invoice Service
 * 
 * All operations are scoped by tenant_id
 */
export const tenantInvoiceService = {
  async getAll(tenantId: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as Invoice[];
  },

  async getById(tenantId: string, id: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (error) throw error;
    return data as Invoice;
  },

  async getByReference(tenantId: string, referenceId: string, referenceType: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('tenant_id', tenantId)
      .eq('reference_id', referenceId)
      .eq('reference_type', referenceType)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as Invoice;
  },

  async create(tenantId: string, invoice: Omit<Invoice, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'patient' | 'items' | 'payments'>) {
    // Generate invoice number
    const invoiceNumber = `INV-${Date.now()}`;
    
    const { data, error } = await insertWithTenant(
      'invoices',
      { 
        ...invoice, 
        invoice_number: invoiceNumber,
        updated_at: new Date().toISOString() 
      },
      tenantId
    );
    
    if (error) throw error;
    
    const { data: fullData, error: fetchError } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('id', data[0].id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (fetchError) throw fetchError;
    return fullData as Invoice;
  },

  async update(tenantId: string, id: string, invoice: Partial<Invoice>) {
    const { data, error } = await updateWithTenant(
      'invoices',
      id,
      { ...invoice, updated_at: new Date().toISOString() },
      tenantId
    );
    
    if (error) throw error;
    
    const { data: fullData, error: fetchError } = await supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*),
        items:invoice_items(*),
        payments:payments(*)
      `)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();
    
    if (fetchError) throw fetchError;
    return fullData as Invoice;
  },

  async delete(tenantId: string, id: string) {
    const { error } = await deleteWithTenant('invoices', id, tenantId);
    if (error) throw error;
  },

  async addItem(tenantId: string, invoiceId: string, item: Omit<InvoiceItem, 'id' | 'tenant_id' | 'invoice_id' | 'total' | 'created_at'>) {
    const { data, error } = await insertWithTenant(
      'invoice_items',
      { ...item, invoice_id: invoiceId },
      tenantId
    );
    
    if (error) throw error;
    return data[0] as InvoiceItem;
  },

  async updateItem(tenantId: string, id: string, item: Partial<InvoiceItem>) {
    const { data, error } = await updateWithTenant(
      'invoice_items',
      id,
      item,
      tenantId
    );
    
    if (error) throw error;
    return data[0] as InvoiceItem;
  },

  async deleteItem(tenantId: string, id: string) {
    const { error } = await deleteWithTenant('invoice_items', id, tenantId);
    if (error) throw error;
  }
};

/**
 * Tenant-Aware Payment Service
 * 
 * All operations are scoped by tenant_id
 */
export const tenantPaymentService = {
  async create(tenantId: string, payment: Omit<Payment, 'id' | 'tenant_id' | 'created_at'>) {
    const { data, error } = await insertWithTenant(
      'payments',
      payment,
      tenantId
    );
    
    if (error) throw error;
    return data[0] as Payment;
  }
};
